import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Clock, CheckCircle2, Copy, Library, Bookmark, Star, RefreshCw, Languages } from "lucide-react";
import { toast } from "sonner";
import { SOURCE_META, THEME_META, formatDate } from "../../lib/catalog";

export default function ArticleCard({
  article, index, saved = [], onToggleSave, opened = false,
  editor = false, themes = [], onPatch,
}) {
  const src = SOURCE_META[article.source];
  const th = THEME_META[article.theme];
  const isCollection = article.is_collection;
  const isSaved = saved.includes(article.slug);
  const [showIt, setShowIt] = useState(false);
  const hasIt = article.lang === "EN" && article.excerpt_it;

  const copyLink = (e) => {
    e.preventDefault();
    e.stopPropagation();
    navigator.clipboard.writeText(article.url);
    toast.success("Link copied to clipboard");
  };

  return (
    <motion.div
      data-testid={`article-card-${article.slug}`}
      layout
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: opened ? 0.68 : 1, y: 0 }}
      whileHover={{ opacity: 1 }}
      transition={{ delay: Math.min(index, 8) * 0.04, duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
      className={`group relative flex flex-col bg-[var(--surface)] border border-[var(--line)] hover:border-[var(--gold)] hover:bg-[var(--surface-2)] transition-[background-color,border-color,opacity] duration-300 p-5`}
    >
      <span className="absolute top-0 left-0 h-full w-[2px]" style={{ backgroundColor: th.color }} />
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="meta-label px-2 py-0.5 border text-[0.52rem]"
            style={{ background: src.bg, borderColor: src.border, color: src.text }}>
            {src.label}
          </span>
          {article.lang === "IT" && (
            <span className="meta-label text-[0.5rem] text-[var(--gold)] border border-[var(--gold)]/50 px-1.5 py-0.5">IT</span>
          )}
          {opened && (
            <span data-testid={`opened-${article.slug}`} className="meta-label text-[0.5rem] text-[var(--muted)] flex items-center gap-1">
              <CheckCircle2 size={10} /> Opened
            </span>
          )}
          {article.auto && (
            <span className="meta-label text-[0.5rem] text-[var(--muted)] border border-[var(--line)] px-1.5 py-0.5 flex items-center gap-1" title="Auto-synced from RSS">
              <RefreshCw size={8} /> SYNC
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {article.featured && <Star size={12} className="text-[var(--gold)]" fill="currentColor" />}
          <span data-testid={`theme-code-${article.slug}`} className="meta-label text-[0.54rem]" style={{ color: th.color }}>{th.code}</span>
          <button data-testid={`save-${article.slug}`} aria-label="Save to reading list"
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); onToggleSave(article.slug); }}
            className="transition-colors"
            style={{ color: isSaved ? "var(--gold)" : "var(--muted)" }}>
            <Bookmark size={14} fill={isSaved ? "currentColor" : "none"} />
          </button>
        </div>
      </div>

      <a data-testid={`article-link-${article.slug}`} href={article.url} target="_blank" rel="noopener noreferrer" className="group/title mt-4">
        <h3 className="font-serif text-[1.35rem] leading-[1.12] text-[var(--txt)]">
          <span className="gold-underline">{article.title}</span>
        </h3>
      </a>
      <p className="mt-2.5 text-[0.82rem] text-[var(--txt-2)] leading-relaxed line-clamp-3 flex-1">
        {article.excerpt}
      </p>

      {hasIt && (
        <div className="mt-2.5">
          <button
            data-testid={`it-summary-toggle-${article.slug}`}
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); setShowIt((v) => !v); }}
            className="flex items-center gap-1.5 meta-label text-[0.52rem] transition-colors"
            style={{ color: showIt ? "var(--gold)" : "var(--muted)" }}
          >
            <Languages size={12} /> {showIt ? "Nascondi" : "In italiano"}
          </button>
          <AnimatePresence>
            {showIt && (
              <motion.p
                data-testid={`it-summary-${article.slug}`}
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: "auto", opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="overflow-hidden text-[0.8rem] italic text-[var(--txt-2)] leading-relaxed mt-2 pl-3 border-l-2 border-[var(--gold)]/50"
              >
                {article.excerpt_it}
              </motion.p>
            )}
          </AnimatePresence>
        </div>
      )}

      {editor && (
        <div className="mt-4 flex items-center gap-2 border-t border-[var(--line-2)] pt-3" onClick={(e) => e.stopPropagation()}>
          <select
            data-testid={`theme-select-${article.slug}`}
            value={article.theme}
            onChange={(e) => onPatch(article.slug, { theme: e.target.value })}
            className="flex-1 bg-[var(--ink)] border border-[var(--line-2)] text-[0.72rem] text-[var(--txt)] px-2 py-1.5 outline-none focus:border-[var(--gold)]"
          >
            {themes.map((t) => (
              <option key={t.id} value={t.id}>{t.label}</option>
            ))}
          </select>
          <button
            data-testid={`feature-toggle-${article.slug}`}
            onClick={() => onPatch(article.slug, { featured: !article.featured })}
            className="meta-label text-[0.54rem] px-2.5 py-1.5 border flex items-center gap-1 transition-colors"
            style={{
              borderColor: article.featured ? "var(--gold)" : "var(--line-2)",
              color: article.featured ? "var(--ink)" : "var(--muted)",
              backgroundColor: article.featured ? "var(--gold)" : "transparent",
            }}
          >
            <Star size={11} fill={article.featured ? "currentColor" : "none"} />
            {article.featured ? "Featured" : "Feature"}
          </button>
        </div>
      )}

      <div className="flex items-center justify-between mt-5 pt-3.5 border-t border-[var(--line)]">
        <span className="meta-label text-[var(--muted)] text-[0.54rem] flex items-center gap-2.5">
          {isCollection ? (
            <span className="flex items-center gap-1 text-[var(--gold)]"><Library size={11} /> Collection</span>
          ) : (
            <>
              {formatDate(article.date)}
              {article.read_min > 0 && (
                <span className="flex items-center gap-1"><Clock size={10} /> {article.read_min}m</span>
              )}
            </>
          )}
        </span>
        <div className="flex items-center gap-2.5">
          <button data-testid={`copy-${article.slug}`} onClick={copyLink} aria-label="Copy link"
            className="text-[var(--muted)] hover:text-[var(--gold)] transition-colors opacity-0 group-hover:opacity-100">
            <Copy size={13} />
          </button>
          <a href={article.url} target="_blank" rel="noopener noreferrer"
            className="flex items-center gap-1 meta-label text-[0.54rem] text-[var(--gold)] hover:gap-2 transition-all">
            Read <ArrowUpRight size={13} />
          </a>
        </div>
      </div>
    </motion.div>
  );
}
